import { useCallback } from "react";
import { listProjectItems, type ListedProjectItem } from "../../../api/content/authoring/processAuthoringApi";
import { readStepCatalogue, type StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import { isAbortError } from "../../../api/http/httpClient";
import { toUiError, type UiError } from "../../../errors/uiError";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/**
 * What the forms offer (AU-3): the project's items at `main` (tools, prompts, skills, label
 * lists, artifacts, steps, Workflows), and the step catalogue from the server. A catalogue
 * the server cannot give does not block the form: the form then offers only what the
 * draft already names, and says why.
 */
export interface AuthoringOptions {
  /** The project the items belong to, which a form's library picker searches. */
  readonly projectId: string;
  readonly items: readonly ListedProjectItem[];
  /** The commit of `main` the items were listed at. */
  readonly mainVersion: string | null;
  readonly catalogue: { readonly status: "loaded"; readonly value: StepCatalogue } | { readonly status: "failed"; readonly error: UiError };
}

async function loadOptions(projectId: string, signal: AbortSignal): Promise<AuthoringOptions> {
  const [listing, catalogue] = await Promise.all([
    listProjectItems(projectId, null, signal),
    readStepCatalogue(projectId, signal).then(
      (value) => ({ status: "loaded" as const, value }),
      (caught: unknown) => {
        if (isAbortError(caught)) {
          throw caught;
        }
        return { status: "failed" as const, error: toUiError(caught) };
      },
    ),
  ]);
  return { projectId, items: listing.items, mainVersion: listing.version, catalogue };
}

export function useAuthoringOptions(projectId: string): { readonly state: LoadState<AuthoringOptions>; readonly reload: () => void } {
  const load = useCallback((signal: AbortSignal) => loadOptions(projectId, signal), [projectId]);
  const { state, reload } = useLoad(load);
  return { state, reload };
}

/** The project's items of one kind, by name. */
export function itemsOfKind(options: AuthoringOptions, kind: ItemKind): readonly ListedProjectItem[] {
  return options.items.filter((item) => item.kind === kind).sort((left, right) => left.name.localeCompare(right.name));
}

/** The listed item an item ID or `VersionRef` names, if it is at `main`. */
export function listedItem(options: AuthoringOptions, itemOrReference: string): ListedProjectItem | undefined {
  const itemId = itemOrReference.split("@")[0];
  return options.items.find((item) => item.itemId === itemId);
}
